import type { Container } from '@packages/core/container';

import { StsBackendClient } from '@packages/clients';
import { ANALYTICS_EVENTS, AnalyticsService } from '@packages/core/analytics';

import { StsBackendOperation } from '../../../sts-backend-operation';
import {
    buildAssetUploadPayload,
    determineAssetType,
    mapCaseStatusToUploadStatus,
    uploadAssetToPresignedUrl,
} from '../helpers';

/** Parameters for uploading a new asset to a Techline case. */
export type UploadAssetParams = {
    /** The file to upload. */
    file: File;

    /** The case this asset belongs to. */
    caseNumber: string;

    /** The current case status code. */
    caseStatusCode: string;

    /** Optional progress callback, called with percentage (0-100). */
    onProgress?: (percent: number) => void;
};

/**
 * Upload a new asset to a techline case. Determines the asset type from
 * the file's MIME type, creates the asset record via the API, then uploads
 * the file to the presigned URL.
 */
export class UploadAsset extends StsBackendOperation {
    /**
     * Register this operation and its dependencies in the IoC container.
     * Overrides the base registration because this operation needs both
     * StsBackendClient and AnalyticsService.
     *
     * @param container - the container to register in
     */
    static override registerInContainer(container: Container): void {
        container.register(UploadAsset, [StsBackendClient, AnalyticsService]);
    }

    /**
     * @param client - the backend API client
     * @param analyticsService - the analytics service for logging events
     */
    constructor(
        client: StsBackendClient,
        private analyticsService: AnalyticsService
    ) {
        super(client);
    }

    /**
     * Create an asset record and upload the file.
     *
     * @param params - the file, case number, status, and optional progress callback
     * @throws {Error} when the API does not return upload parameters or an asset ID
     * @returns the server-assigned asset ID for the newly created asset
     */
    async exec(params: UploadAssetParams): Promise<string> {
        const { file, caseNumber, caseStatusCode, onProgress } = params;

        this.analyticsService.logEvent(ANALYTICS_EVENTS.USER_STARTS_ATTACHMENT_FLOW);

        // Determine the asset type and upload status from inputs
        const assetType = determineAssetType(file.type);
        const uploadStatus = mapCaseStatusToUploadStatus(caseStatusCode);
        const payload = buildAssetUploadPayload(file, caseNumber, assetType, uploadStatus);

        // Create the asset record and get presigned upload parameters
        const postResponse = await this.client.postAssets({ assetsBody: payload });
        const assetResult = postResponse.success ? postResponse.data.results?.assets?.[0] : undefined;
        const uploadParams = assetResult?.presignedUpload;
        const assetId = assetResult?.asset?.assetId;

        if (!uploadParams || !assetId) {
            throw new Error('No upload parameters or asset ID received.');
        }

        // Upload the file to the presigned URL
        await uploadAssetToPresignedUrl({
            uploadUrl: uploadParams.url,
            headers: uploadParams.httpHeaders,
            body: file,
            onProgress,
        });

        this.analyticsService.logEvent(ANALYTICS_EVENTS.USER_SUCCESSFULLY_UPLOADS_ATTACHMENT);

        return assetId;
    }
}
